import { NEDGLOBE_MAP_PROJECTION, NEDGLOBE_MAP_RESOLUTION, GEOLOCATION_POSITION_TIMEOUT, NEDGLOBE_VIEWER, GPS_MARKER_TYPE } from '../../../constants';
import { registerNedGlobeMap, unRegisterNedGlobeMap, polygonOutline, featureMask, init3DMapObject } from '../../../services/map';
import { createMarker, createMarkerGps, getGpsDefaultStyle, getGpsRoseStyle, getGpsRoseSelectedStyle, getGpsDirectionStyle } from '../../../services/map/layer/marker';
import { createCustomLayers } from '../../../services/map/layer/selections';
import { getStyle } from '../../../services/map/layer/styles';
/* OpenLayers imports */
import OlGeolocation from 'ol/Geolocation';
import OlPoint from 'ol/geom/Point';

const getMapConfig = (state, mapName) => {
    mapName = mapName || state.defaultMapName;
    const mapConfig = state.maps.find(_map => _map.name === mapName);
    if (!mapConfig) {
        throw new Error('map not found');
    }
    return mapConfig;
};

export const mutations = {
    MAP_READY(state, { name, ready }) {
        const mapConfig = getMapConfig(state, name);
        mapConfig.mapReady = ready;
    },
    REGISTER_MAP(state, { name, id, projection = NEDGLOBE_MAP_PROJECTION, resolution = NEDGLOBE_MAP_RESOLUTION, interactions, controls }) {
        name = name || state.defaultMapName;
        const map = registerNedGlobeMap(id, state.userData, interactions, controls);
        const mapConfig = {
            name,
            map,
            mapReady: false,
            // a layer for the background
            baseLayer: null,
            // a list of layers displayed in the map originating from either a theme or feedback
            layers: [],
            customLayers: createCustomLayers(map),
            projection,
            resolution,
            highlight: null,
            viewport: { resolution: map.getView().getResolution() },
            gpsTracker: new OlGeolocation({
                projection: map.getView().getProjection(),
                tracking: false,
                trackingOptions: {
                    timeout: GEOLOCATION_POSITION_TIMEOUT,
                    enableHighAccuracy: true,
                    maximumAge: 0
                }
            })
        };
        const maps = state.maps.filter(_map => _map.name !== name);
        state.maps = [...maps, mapConfig];
        if (name === state.defaultMapName) {
            state.defaultMap = mapConfig;
        }

        // for now do not init 3D for web
        if (state.webThemeParameters?.tma) {
            state.map3D = null;
            return;
        }

        const map3D = init3DMapObject(map);
        state.map3D = map3D;
    },
    SET_MAP3D_ENABLED(state, { enabled }) {
        state.map3DEnabled = enabled;
    },
    UNREGISTER_MAP(state, { name }) {
        const mapConfig = getMapConfig(state, name);
        unRegisterNedGlobeMap(mapConfig.map);
        const maps = state.maps.filter(_map => _map.name !== name);
        state.maps = maps;
    },
    MAP_ADD_HIGHLIGHT_BORDER(state, { mapName, coords }) {
        const mapConfig = getMapConfig(state, mapName);
        if (!coords) {
            throw new Error('no coords found');
        }
        mapConfig.highlight = coords;
        const drawingLayer = mapConfig.customLayers.organizationHighlightBorder;
        const drawingLayerSource = drawingLayer.getSource();
        drawingLayerSource.clear();
        const feature = polygonOutline(drawingLayer, coords);
        if (feature) {
            drawingLayer.addFilter(featureMask(feature));
            drawingLayerSource.addFeature(feature);
        }
    },
    MAP_ADD_DRAWING(state, { mapName, feature }) {
        const mapConfig = getMapConfig(state, mapName);
        const drawingLayer = mapConfig.customLayers.drawing;
        const drawingLayerSource = drawingLayer.getSource();
        drawingLayerSource.clear();
        if (feature) {
            drawingLayerSource.addFeature(feature);
        }
    },
    MAP_SET_SELECTON_AREA(state, { mapName, feature }) {
        const mapConfig = getMapConfig(state, mapName);
        const drawingLayer = mapConfig.customLayers.selectionArea;
        const drawingLayerSource = drawingLayer.getSource();
        drawingLayerSource.clear();
        const selectionFeature = feature && polygonOutline(drawingLayer, feature.getGeometry().getCoordinates()[0], 'rgba(0,122,201,1)', 0);
        drawingLayer.getFilters().forEach(filter => drawingLayer.removeFilter(filter));
        if (selectionFeature) {
            drawingLayer.addFilter(featureMask(selectionFeature, [256, 256, 256, 0.6]));
            drawingLayerSource.addFeature(selectionFeature);
        }
    },
    MAP_ADD_MARKER(state, { mapName, point }) {
        const mapConfig = getMapConfig(state, mapName);
        const markerLayer = mapConfig.customLayers.marker;
        const markerLayerSource = markerLayer.getSource();
        markerLayerSource.clear();
        if (point) {
            const marker = createMarker(point);
            markerLayerSource.addFeature(marker);
        }
    },
    MAP_ADD_MARKER_GPS(state, { mapName }) {
        const mapConfig = getMapConfig(state, mapName);
        const markerLayerGPS = mapConfig.customLayers.markerGPS;
        const markerLayerSource = markerLayerGPS.getSource();
        markerLayerSource.clear();

        const marker = createMarkerGps();
        markerLayerSource.addFeature(marker);
    },
    MARKER_GPS_UPDATE_ROTATION(state, { mapName, rotation }) {
        const mapConfig = getMapConfig(state, mapName);
        const markerLayerGPS = mapConfig.customLayers.markerGPS;
        const markerLayerSource = markerLayerGPS.getSource();
        const marker = markerLayerSource.getFeatureById('markerGps');
        if (marker) {
            const markerStyle = marker.getStyle();
            if (markerStyle.getImage) {
                markerStyle.getImage().setRotation(rotation);
                // Tell the source to update view
                markerLayerSource.changed();
            }
        }
    },
    MARKER_GPS_UPDATE_GEOMETRY(state, { mapName, point }) {
        const mapConfig = getMapConfig(state, mapName);
        const markerLayerGPS = mapConfig.customLayers.markerGPS;
        const markerLayerSource = markerLayerGPS.getSource();
        const marker = markerLayerSource.getFeatureById('markerGps');
        if (marker) {
            marker.setGeometry(new OlPoint(point));
        }
    },
    MARKER_GPS_CHANGE_ICON(state, { mapName, typeMarker }) {
        const mapConfig = getMapConfig(state, mapName);
        const markerLayerGPS = mapConfig.customLayers.markerGPS;
        const markerLayerSource = markerLayerGPS.getSource();
        const marker = markerLayerSource.getFeatureById('markerGps');
        if (marker) {
            if (typeMarker === GPS_MARKER_TYPE.Rose) {
                marker.setStyle(getGpsRoseStyle());
            } else if (typeMarker === GPS_MARKER_TYPE.RoseSelected) {
                marker.setStyle(getGpsRoseSelectedStyle());
            } else if (typeMarker === GPS_MARKER_TYPE.Direction) {
                marker.setStyle(getGpsDirectionStyle());
            } else {
                marker.setStyle(getGpsDefaultStyle());
            }
        }
    },
    MAP_MOVE_END(state, { mapName }) {
        const mapConfig = getMapConfig(state, mapName);
        const map = mapConfig.map;
        const viewportZoom = map.getView().getZoom();
        const viewportCenter = map.getView().getCenter();
        if (typeof viewportZoom === 'number' && viewportCenter) {
            const viewportData = {
                center: viewportCenter,
                zoom: viewportZoom,
                resolution: map.getView().getResolution()
            };
            mapConfig.viewport = { ...mapConfig.viewport, ...viewportData };
        }

    },
    REMOVE_BASE_LAYER_FROM_MAP(state, { mapName }) {
        const mapConfig = getMapConfig(state, mapName);

        if (mapConfig.baseLayer) {
            mapConfig.map.removeLayer(mapConfig.baseLayer.olLayer);
            mapConfig.baseLayer = null;
        }
    },
    SET_BASE_LAYER_TO_MAP(state, { mapName, id, olLayer, terms }) {
        const mapConfig = getMapConfig(state, mapName);

        if (!id) {
            throw new Error('id not found');
        }
        if (!olLayer) {
            throw new Error('olLayer not found');
        }
        olLayer.setZIndex(-1);
        const layer = {
            id: id,
            olLayer,
            terms
        };
        if (mapConfig.baseLayer) {
            mapConfig.map.removeLayer(mapConfig.baseLayer.olLayer);
        }
        state.layers
            .filter(({ type }) => type === 'base')
            .forEach(state_layer => {
                state_layer.active = state_layer.id === id;
            });
        mapConfig.baseLayer = layer;
        mapConfig.map.addLayer(olLayer);
    },
    ADD_LAYER_TO_MAP(state, { mapName, id, olLayer, minResolution, maxResolution, terms }) {
        const mapConfig = getMapConfig(state, mapName);
        if (!id) {
            throw new Error('id not found');
        }
        if (!olLayer) {
            throw new Error('olLayer not found');
        }
        let layer = mapConfig.layers.find(_layer => _layer.id === id);
        // if the layer existst, remove from openlayers
        if (layer) {
            mapConfig.map.removeLayer(layer.olLayer);
        }
        if (minResolution > 0) {
            olLayer.setMinResolution(minResolution);
        }
        if (maxResolution > 0) {
            olLayer.setMaxResolution(maxResolution);
        }
        layer = {
            visible: null,
            id: id,
            olLayer,
            minResolution,
            maxResolution,
            terms
        };
        mapConfig.layers = [
            ...mapConfig.layers.filter(_layer => _layer.id !== id),
            layer
        ];
    },
    DISPLAY_LAYER(state, { mapName, id }) {
        mapName = mapName || state.defaultMapName;
        if (!id) {
            throw new Error('invalid id');
        }
        const mapConfig = state.maps.find(_map => _map.name === mapName);
        if (!mapConfig) {
            throw new Error('map not found');
        }
        const layer = mapConfig.layers.find(_layer => _layer.id === id);
        if (!layer) {
            throw new Error('layer not found');
        }
        const olLayer = layer.olLayer;
        if (!olLayer) {
            throw new Error('openlayers layer not found');
        }

        mapConfig.map.removeLayer(olLayer);
        mapConfig.map.addLayer(olLayer);
        if (mapName === state.defaultMapName) {
            // set visible in mapcontexct
            mapConfig.layers = mapConfig.layers.map(mapConfig_layer => ({
                ...mapConfig_layer,
                visible: mapConfig_layer.id === id ? true : mapConfig_layer.visible
            }));
            // set active in layers context
            state.layers = state.layers.map(state_layer => ({
                ...state_layer,
                active: state_layer.id === id ? true : state_layer.active
            }));
        }
    },
    MAP_LAYER_OPACITY(state, { mapName, id, opacity }) {
        mapName = mapName || state.defaultMapName;
        if (!id) {
            throw new Error('invalid id');
        }
        const mapConfig = state.maps.find(_map => _map.name === mapName);
        if (!mapConfig) {
            throw new Error('map not found');
        }
        const layer = mapConfig.layers.find(_layer => _layer.id === id);
        if (!layer) {
            throw new Error('layer not found');
        }
        const olLayer = layer.olLayer;
        if (!olLayer) {
            throw new Error('openlayers layer not found');
        }
        olLayer.setOpacity(opacity);

    },
    HIDE_LAYER(state, { mapName, id }) {
        mapName = mapName || state.defaultMapName;
        if (!id) {
            throw new Error('invalid id');
        }
        const mapConfig = getMapConfig(state, mapName);
        const layer = mapConfig.layers.find(_layer => _layer.id === id);
        if (!layer) {
            return;
        }
        if (!layer.visible) {
            return;
        }
        const olLayer = layer.olLayer;
        if (!olLayer) {
            throw new Error('openlayers layer not found');
        }
        mapConfig.map.removeLayer(olLayer);

        // if layer with a graphical layer has a textattribute style, then remove the extra added layer
        const layerWithStyle = mapConfig.layers.find(_layer => _layer.id === `${id}_style`);
        if (layerWithStyle) {
            const olLayerWithStyle = layerWithStyle.olLayer;
            if (olLayerWithStyle) {
                mapConfig.map.removeLayer(olLayerWithStyle);
            }
        }

        if (mapName === state.defaultMapName) {
            // set visible in mapcontexct
            mapConfig.layers = mapConfig.layers.map(mapConfig_layer => ({
                ...mapConfig_layer,
                visible: mapConfig_layer.id === id ? false : mapConfig_layer.visible
            }));
            // set active in layers context
            state.layers = state.layers.map(state_layer => ({
                ...state_layer,
                active: state_layer.id === id ? false : state_layer.active
            }));
        }
    },
    UPDATE_MAP_LAYER_ZINDEXES(state, { mapName, layers }) {
        const mapConfig = getMapConfig(state, mapName);
        layers.forEach((layer) => {
            const foundLayer = mapConfig?.layers?.find(ml => ml.id === layer.id);
            if (foundLayer) {
                foundLayer.olLayer.setZIndex((layer.order));
            }
        });

    },
    MAP_POINTER_DRAG(state, { mapName }) {
        // An empty commit function suitable for implementations elsewhere
        // like subscriptions in store/nedGlobe/[appname]/base
    },
    MAP_CLICKED(state, { mapName }) {
        // An empty commit function suitable for implementations elsewhere
        // like subscriptions in store/nedGlobe/[appname]/base
    },
    MAP_DOUBLE_CLICKED(state, { mapName }) {
        // An empty commit function suitable for implementations elsewhere
        // like subscriptions in store/nedGlobe/[appname]/base
    },
    MAP_ZOOM_CHANGE(state, { mapName, zoom }) {
        const mapConfig = getMapConfig(state, mapName);
        mapConfig.viewport.zoom = zoom;
        mapConfig.viewport.resolution = mapConfig.map.getView().getResolution();
    },
    MAP_START_INTERACTION(state, { mapName, coordinate }) {
        // An empty commit function suitable for implementations elsewhere
        // like subscriptions in store/nedGlobe/[appname]/base
    },
    MAP_POINTER_DOWN_SHORT(state, { mapName, coordinate, button, event }) {
        // An empty commit function suitable for implementations elsewhere
        // like subscriptions in store/nedGlobe/[appname]/base
    },
    MAP_POINTER_DOWN_LONG(state, { mapName, coordinate, button, event }) {
        // An empty commit function suitable for implementations elsewhere
        // like subscriptions in store/nedGlobe/[appname]/base
    },
    MAP_POINTER_UP_SHORT(state, { mapName, coordinate, button, event }) {
        // An empty commit function suitable for implementations elsewhere
        // like subscriptions in store/nedGlobe/[appname]/base
    },
    MAP_POINTER_UP_LONG(state, { mapName, coordinate, button, event }) {
        // An empty commit function suitable for implementations elsewhere
        // like subscriptions in store/nedGlobe/[appname]/base
    },
    MAP_SELECT_CLUSTER(state, { mapName, features }) {
        // An empty commit function suitable for implementations elsewhere
        // like subscriptions in store/nedGlobe/[appname]/base
    },
    MAP_TOGGLE_FOCUS(state, { mapName, active }) {
        const mapConfig = getMapConfig(state, mapName);
        mapConfig.layers.forEach(layer => {
            layer.olLayer.setOpacity(active ? 0.5 : 1);
        });
    },
    RESET_MAP_FEATURES(state, { mapName, customLayerName }) {
        const mapConfig = getMapConfig(state, mapName);
        const selectionLayer = mapConfig.customLayers[customLayerName || NEDGLOBE_VIEWER.customLayers.default];
        const selectionsLayerSource = selectionLayer.getSource();
        selectionsLayerSource.clear();
    },
    SHOW_MAP_FEATURE(state, { mapName, customLayerName, feature, features, style, reset, computedStyles, styleType }) {
        const mapConfig = getMapConfig(state, mapName);
        const selectionLayer = mapConfig.customLayers[customLayerName || NEDGLOBE_VIEWER.customLayers.default];
        const selectionsLayerSource = selectionLayer.getSource();
        const getComputedFeatureStyleFunction = (_feature, resolution) => {
            return getStyle(
                computedStyles,
                _feature,
                resolution,
                styleType || NEDGLOBE_VIEWER.styleDisplayTypes.default
            );
        };
        if (reset) {
            selectionsLayerSource.clear();
        }
        if (feature) {
            if (style) {
                feature.setStyle(style);
            } else if (computedStyles) {
                feature.setStyle(getComputedFeatureStyleFunction);
            }
            selectionsLayerSource.addFeature(feature);
        }
        if (features) {
            if (style) {
                features.forEach(_feature => {
                    _feature.setStyle(style);
                });
            } else if (computedStyles) {
                features.forEach(_feature => {
                    _feature.setStyle(getComputedFeatureStyleFunction);
                });

            }
            selectionsLayerSource.addFeatures(features);
        }
    },
    SET_SCALES(state, { scales }) {
        state.scales = scales;
    },
    SET_MAP_TOOLTIPS_ENABLED(state, { enabled }) {
        state.mapTooltipsEnabled = enabled;
    },
    SET_APPLYING_USERMAP(state, { applying }) {
        state.applyingUserMap = applying;
    }
};
